import React, { useContext, useEffect, useState } from 'react'
import { makeStyles, Toolbar, Typography } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import SearchIcon from '@material-ui/icons/Search'
import { useDebounce } from 'use-debounce'
import * as _ from 'lodash'
import { Context as PortfolioContext } from '../contexts/portfolio'
import FilterBoxCommon from './FilterBoxCommon'
import countryData from '../data/countryData.json'
import {
  filterTickers,
  impliedRatingComparator,
  INIT_FILTER_STATE,
} from '../ultils/dataUtil'
import {
  commonMapFilter,
  creditRiskStatusData,
  riskStatusData,
} from '../ultils/chart-d3-utils'

export const initFilterLable = {
  impliedRating: 'Implied Rating',
  status: 'Risk Status',
  sector: 'Sector',
  country: 'Country',
  region: 'Country',
}

const useStyles = makeStyles({
  toolbarFilterBar: {
    padding: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 'unset',
    paddingBottom: 20,
    borderBottom: '1px solid #DFE4EB',
  },
  title: {
    flex: '1 1 100%',
    fontSize: 20,
    lineHeight: '24.5px',
    color: '#181818',
    marginRight: 34,
  },
  search: {
    display: 'flex',
    alignItems: 'center',
  },
  filter: {
    display: 'flex',
    alignItems: 'center',
    '& > span': {
      color: '#474747',
      fontWeight: 600,
      fontSize: 14,
    },
  },
  searchName: {
    minWidth: 300,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#F4F5F7',
    borderRadius: 3,
    padding: 12,
    '& svg': {
      width: 16,
      height: 16,
      color: '#474747',
    },
    '& input': {
      border: 'none',
      background: 'transparent',
      paddingLeft: 8,
      fontSize: 14,
      lineHeight: '22px',
      fontWeight: 400,
      outline: 'none',
      width: 'calc(100% - 20px)',

      '&::placeholder': {
        fontSize: 14,
        lineHeight: '22px',
        fontWeight: 400,
        color: '#757575',
      },
    },
  },
  reset: {
    color: '#1B3963 !important',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 20,
    cursor: 'pointer',
  },
  resetText: {
    lineHeight: '22px',
    fontWeight: 400,
  },
  ReplayIcon: {
    width: 18,
    height: 18,
    marginRight: 2,
  },
  dropdownStatus: {
    marginLeft: 16,
  },
  dropdownType: {
    marginLeft: 8,
  },
  dropdownLocation: {
    marginLeft: 8,
  },
  filterBy: {
    fontSize: 14,
    lineHeight: '22px',
    fontWeight: 600,
    color: '#474747',
  },
})
interface FilterProps {
  tickers: any
  hideFilter: boolean
}

const FilterPortfolioTable = ({ tickers, hideFilter }: FilterProps) => {
  const classes = useStyles()
  const [reset, setReset] = useState(false)
  const {
    state,
    setLoading,
    updatePortfolioData,
    changeSearchInputValue,
    changeRiskStatus,
    changeCreditRiskStatus,
    changeImpliedRating,
    changeSector,
    changeCountry,
    changeRegion,
  } = useContext(PortfolioContext)
  const [searchByName, setSearchByName] = useState<any>(
    state.filterState.searchByWord,
  )
  const [keyWord] = useDebounce(searchByName, 500)
  const getDynamicFilterData = (staticList: any, dynamicList: any) => {
    dynamicList.forEach((item: any) => {
      staticList.forEach((innerItem: any) => {
        if (item.value === innerItem.value) {
          innerItem.isChecked = item.isChecked
        }
      })
    })
    return staticList
  }

  const uniqueSectors: any = [
    ...new Set(tickers.map((t: any) => t.factset_sector_desc)),
  ]
  const sectorData = uniqueSectors.map((value: any, id: any) => ({
    id,
    value,
    isChecked: false,
  }))

  let riskData = _.cloneDeep(riskStatusData)
  let creditRiskData = _.cloneDeep(creditRiskStatusData)
  const uniqueRegions: any = [...new Set(tickers.map((t: any) => t.region))]
  const regionData = uniqueRegions.map((value: any, id: any) => ({
    id,
    value,
    isChecked: false,
  }))

  const uniqueImpliedRatings: any = [
    ...new Set(
      tickers
        .map((t: any) => t.bfaiRating)
        .sort((n1: string, n2: string) => {
          return impliedRatingComparator(n1, n2)
        })
        .reverse(),
    ),
  ]
  const impliedRatingListData = uniqueImpliedRatings.map(
    (value: any, id: any) => ({
      id,
      value,
      isChecked: false,
    }),
  )

  const [impliedRating, setImpliedRating] = useState<any>([])
  const [riskStatus, setRiskStatus] = useState<any>([])
  const [creditRiskStatus, setCreditRiskStatus] = useState<any>([])
  const [sector, setSector] = useState<any>([])
  const [region, setRegion] = useState<any>([])
  const [country, setCountry] = useState<any>([])
  const [tickerData, setTickerData] = useState(tickers)
  const [initFilterTitle, setInitFilterTitle] = useState(INIT_FILTER_STATE)
  const [internalImpliedRatingData, setInternalImpliedRatingData] = useState(
    getDynamicFilterData(
      impliedRatingListData,
      state.filterState.impliedRating,
    ),
  )
  const [internalRiskStatusData, setInternalRiskStatusData] = useState(
    getDynamicFilterData(riskData, state.filterState.riskStatus),
  )
  const [
    internalCreditRiskStatusData,
    setInternalCreditRiskStatusData,
  ] = useState(
    getDynamicFilterData(creditRiskData, state.filterState.creditRiskStatus),
  )
  const [internalSectorData, setInternalSectorData] = useState(
    getDynamicFilterData(sectorData, state.filterState.sector),
  )
  const [internalRegionData, setInternalRegionData] = useState(
    getDynamicFilterData(regionData, state.filterState.region),
  )
  const [internalCountryData, setInternalCountryData] = useState(
    getDynamicFilterData(countryData, state.filterState.country),
  )

  useEffect(() => {
    setImpliedRating(state.filterState.impliedRating)
    setRiskStatus(state.filterState.riskStatus)
    setCreditRiskStatus(state.filterState.creditRiskStatus)
    setSector(state.filterState.sector)
    setRegion(state.filterState.region)
    setCountry(state.filterState.country)
    // const filterTitleState = {
    //   impliedRating:
    //     state.filterState.impliedRating.length === 0
    //       ? 'Implied Rating'
    //       : state.filterState.impliedRating
    //           .map((item: any) => item.value)
    //           .toString(),
    //   status:
    //     state.filterState.riskStatus.length === 0 &&
    //     state.filterState.creditRiskStatus.length === 0
    //       ? 'Risk Status'
    //       : `${state.filterState.riskStatus
    //           .map((item: any) => item.value)
    //           .toString()} , ${state.filterState.creditRiskStatus
    //           .map((item: any) => item.value)
    //           .toString()}`,
    //   sector:
    //     state.filterState.sector.length === 0
    //       ? 'Sector'
    //       : state.filterState.sector.map((item: any) => item.value).toString(),
    //   country:
    //     state.filterState.country.length === 0
    //       ? 'Country'
    //       : state.filterState.country.map((item: any) => item.value).toString(),
    //   region:
    //     state.filterState.region.length === 0
    //       ? 'Country'
    //       : state.filterState.region.map((item: any) => item.value).toString(),
    // }
    // setInitFilterTitle(filterTitleState)
  }, [state.filterState])

  useEffect(() => {
    setInternalImpliedRatingData(
      getDynamicFilterData(
        _.cloneDeep(impliedRatingListData),
        state.filterState.impliedRating,
      ),
    )
    setInternalRiskStatusData(
      getDynamicFilterData(_.cloneDeep(riskData), state.filterState.riskStatus),
    )
    setInternalCreditRiskStatusData(
      getDynamicFilterData(
        _.cloneDeep(creditRiskData),
        state.filterState.creditRiskStatus,
      ),
    )
    setInternalSectorData(
      getDynamicFilterData(_.cloneDeep(sectorData), state.filterState.sector),
    )
    setInternalRegionData(
      getDynamicFilterData(_.cloneDeep(regionData), state.filterState.region),
    )
    setInternalCountryData(
      getDynamicFilterData(_.cloneDeep(countryData), state.filterState.country),
    )
  }, [reset])

  useEffect(() => {
    const filterObj = {
      keyWord,
      sector,
      country,
      region,
      riskStatus,
      creditRiskStatus,
      impliedRating,
    }
    const filterTickersData = commonMapFilter(filterTickers(tickers, filterObj))
    updatePortfolioData(filterTickersData)
    setLoading(false)
    setTickerData(filterTickersData)
  }, [
    keyWord,
    sector,
    country,
    region,
    riskStatus,
    creditRiskStatus,
    impliedRating,
  ])

  const setFilterValue = (val: any, type: any) => {
    if (type === 'riskStatus') {
      const equityList = val.filter((item: any) => item.type === 'equityRisk')
      const creditList = val.filter((item: any) => item.type === 'creditRisk')
      setRiskStatus(equityList)
      changeRiskStatus(equityList)
      setCreditRiskStatus(creditList)
      changeCreditRiskStatus(creditList)
    } else if (type === 'impliedRating') {
      setImpliedRating(val)
      changeImpliedRating(val)
    } else if (type === 'sector') {
      setSector(val)
      changeSector(val)
    } else if (type === 'country') {
      const countryList = val.filter((item: any) => isNaN(item.id))
      const regionList = val.filter((item: any) => !isNaN(item.id))
      setCountry(countryList)
      changeCountry(countryList)
      setRegion(regionList)
      changeRegion(regionList)
    }
    setReset(false)
  }

  const handleReset = () => {
    // window.location.reload()
    setReset(true)
    setInitFilterTitle(initFilterLable)
    riskData = _.cloneDeep(riskStatusData)
    setImpliedRating('')
    setSearchByName('')
    setRiskStatus('')
    setCreditRiskStatus('')
    setSector('')
    setCountry('')
    changeRiskStatus([])
    changeCreditRiskStatus([])
    changeImpliedRating([])
    changeCountry([])
    changeRegion([])
    changeSector([])
  }

  const handleChangeSearchByName = (e: any) => {
    setSearchByName(e.target.value)
    changeSearchInputValue(e.target.value)
  }

  return (
    <Toolbar className={classes.toolbarFilterBar}>
      <div className={classes.search}>
        <Typography className={classes.title} id="tableTitle" component="div">
          Portfolio List
        </Typography>
        {!hideFilter && (
          <div className={classes.searchName}>
            <SearchIcon />
            <input
              value={searchByName}
              onChange={handleChangeSearchByName}
              placeholder="Search by name"
            />
          </div>
        )}
      </div>
      {!hideFilter && (
        <div className={classes.filter}>
          <div className={classes.filterBy}>Filter by</div>
          <div className={classes.dropdownStatus}>
            <FilterBoxCommon
              placeholder={initFilterTitle.impliedRating}
              data={[
                {
                  title: '',
                  list: internalImpliedRatingData,
                  heightScrollList: 212,
                },
              ]}
              setFilterValue={(val: any) =>
                setFilterValue(val, 'impliedRating')
              }
              isSearch={false}
              isRadio={false}
              customStyle={{ width: 150 }}
              isReset={reset}
              itemInfo={[
                impliedRatingListData.map((itemOut: any) => {
                  return {
                    id: itemOut.id,
                    value: tickerData.filter(
                      (item: any) => item.bfaiRating === itemOut.value,
                    ).length,
                    isChecked: reset || itemOut.isChecked,
                  }
                }),
              ]}
            />
          </div>
          <div className={classes.dropdownStatus}>
            <FilterBoxCommon
              placeholder={initFilterTitle.status}
              data={[
                {
                  title: 'Equity Risk',
                  list: internalRiskStatusData,
                  heightScrollList: 212,
                },
                {
                  title: 'Credit Risk',
                  list: internalCreditRiskStatusData,
                  heightScrollList: 212,
                },
              ]}
              setFilterValue={(val: any) => setFilterValue(val, 'riskStatus')}
              isSearch={false}
              isRadio={false}
              customStyle={{ width: 150 }}
              isReset={reset}
              itemInfo={[
                riskData.map((itemOut: any) => {
                  return {
                    id: itemOut.id,
                    value: tickerData.filter(
                      (item: any) => item.status === itemOut.value,
                    ).length,
                    isChecked: reset || itemOut.isChecked,
                    type: itemOut.type,
                  }
                }),
                creditRiskData.map((itemOut: any) => {
                  return {
                    id: itemOut.id,
                    value: tickerData.filter(
                      (item: any) => item.credit_status === itemOut.value,
                    ).length,
                    isChecked: reset || itemOut.isChecked,
                    type: itemOut.type,
                  }
                }),
              ]}
            />
          </div>
          <div className={classes.dropdownType}>
            <FilterBoxCommon
              placeholder={initFilterTitle.sector}
              data={[
                {
                  title: '',
                  list: internalSectorData,
                  heightScrollList: 212,
                },
              ]}
              setFilterValue={(val: any) => setFilterValue(val, 'sector')}
              isSearch={false}
              isRadio={false}
              isReset={reset}
              customStyle={{ width: 150 }}
              itemInfo={[
                sectorData.map((itemOut: any) => {
                  return {
                    id: itemOut.id,
                    value: tickerData.filter(
                      (item: any) => item.factset_sector_desc === itemOut.value,
                    ).length,
                    isChecked: reset || itemOut.isChecked,
                  }
                }),
              ]}
            />
          </div>
          <div className={classes.dropdownLocation}>
            <FilterBoxCommon
              placeholder={initFilterTitle.country}
              data={[
                {
                  title: 'Region',
                  list: internalRegionData,
                  heightScrollList: 212,
                },
                {
                  title: 'Country',
                  list: internalCountryData,
                  heightScrollList: 212,
                },
              ]}
              setFilterValue={(val: any) => {
                setFilterValue(val, 'country')
              }}
              isSearch
              isRadio={false}
              isReset={reset}
              customStyle={{ width: 150 }}
              itemInfo={[
                regionData.map((itemOut: any) => {
                  return {
                    id: itemOut.id,
                    value: tickerData.filter(
                      (item: any) => item.region === itemOut.value,
                    ).length,
                    isChecked: reset || itemOut.isChecked,
                  }
                }),
                countryData.map((itemOut: any) => {
                  return {
                    id: itemOut.id,
                    value: tickerData.filter(
                      (item: any) => item.iso_country === itemOut.id,
                    ).length,
                    isChecked: reset || itemOut.isChecked,
                  }
                }),
              ]}
            />
          </div>

          <span onClick={handleReset} className={classes.reset}>
            <RefreshIcon className={classes.ReplayIcon} />
            <span className={classes.resetText}>Reset</span>
          </span>
        </div>
      )}
    </Toolbar>
  )
}

export default FilterPortfolioTable
